/**
 * Use Notifications Hook
 *
 * Custom hook for accessing the notifications context with helper methods.
 * Wraps the core-components notification context to provide convenience methods.
 *
 * @since 1.0.0
 */

import { useNotification } from '@redfoxwp/core-components/contexts/notification-context';
import { useCallback } from 'react';

import type { Notification } from './types';

/**
 * Notifications context state interface with helper methods
 *
 * @since 1.0.0
 */
export interface NotificationsState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  showSuccess: (
    message: string,
    options?: Omit<Notification, 'id' | 'variant' | 'message'>
  ) => string;
  showError: (
    message: string,
    options?: Omit<Notification, 'id' | 'variant' | 'message'>
  ) => string;
  showWarning: (
    message: string,
    options?: Omit<Notification, 'id' | 'variant' | 'message'>
  ) => string;
  showInfo: (message: string, options?: Omit<Notification, 'id' | 'variant' | 'message'>) => string;
}

/**
 * Hook to use notifications with helper methods
 *
 * @since 1.0.0
 *
 * @return Notifications state with helper methods.
 */
export const useNotifications = (): NotificationsState => {
  const { notifications, addNotification, removeNotification, clearNotifications } =
    useNotification();

  /**
   * Add a notification with custom options
   *
   * @since 1.0.0
   *
   * @param notification Notification configuration.
   *
   * @return Notification ID.
   */
  const addNotificationWrapper = useCallback(
    (notification: Omit<Notification, 'id'>): string => {
      const { variant, message, duration, dismissible } = notification;

      // Convert duration format: false -> autoDismiss: false, number -> use duration
      const options = {
        dismissible: dismissible ?? true,
        autoDismiss: duration !== false,
        duration: typeof duration === 'number' ? duration : 5000,
      };

      return addNotification(variant, message, options);
    },
    [addNotification]
  );

  /**
   * Show success notification
   *
   * @since 1.0.0
   *
   * @param message Notification message.
   * @param options Optional configuration.
   *
   * @return Notification ID.
   */
  const showSuccess = useCallback(
    (message: string, options?: Omit<Notification, 'id' | 'variant' | 'message'>): string => {
      return addNotificationWrapper({
        variant: 'success',
        message,
        ...options,
      });
    },
    [addNotificationWrapper]
  );

  /**
   * Show error notification
   *
   * @since 1.0.0
   *
   * @param message Notification message.
   * @param options Optional configuration.
   *
   * @return Notification ID.
   */
  const showError = useCallback(
    (message: string, options?: Omit<Notification, 'id' | 'variant' | 'message'>): string => {
      return addNotificationWrapper({
        variant: 'error',
        message,
        ...options,
      });
    },
    [addNotificationWrapper]
  );

  /**
   * Show warning notification
   *
   * @since 1.0.0
   *
   * @param message Notification message.
   * @param options Optional configuration.
   *
   * @return Notification ID.
   */
  const showWarning = useCallback(
    (message: string, options?: Omit<Notification, 'id' | 'variant' | 'message'>): string => {
      return addNotificationWrapper({
        variant: 'warning',
        message,
        ...options,
      });
    },
    [addNotificationWrapper]
  );

  /**
   * Show info notification
   *
   * @since 1.0.0
   *
   * @param message Notification message.
   * @param options Optional configuration.
   *
   * @return Notification ID.
   */
  const showInfo = useCallback(
    (message: string, options?: Omit<Notification, 'id' | 'variant' | 'message'>): string => {
      return addNotificationWrapper({
        variant: 'info',
        message,
        ...options,
      });
    },
    [addNotificationWrapper]
  );

  return {
    notifications,
    addNotification: addNotificationWrapper,
    removeNotification,
    clearNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
